/* The figure, listening.
 *
 * The phoniq mascot redrawn in one fragment shader (see figurShader.ts), so it
 * is cheap enough for a phone and needs none of the cables runtime. It moves to
 * the sound through `drive`: a ref the page writes every frame with the level
 * and three bands, each 0..1. A ref and not props, because the analyser runs at
 * 60 Hz and a React render per frame would cost more than the drawing.
 *
 * Everything here is motion, not measurement. The numbers a person reads come
 * from the page, never from how big the figure got.
 */
import { useEffect, useRef } from 'react'
import { FRAG, VERT } from '../figurShader'

export interface Drive { level: number; low: number; mid: number; high: number }

const UNIFORMS = ['uT', 'uAspect', 'uYaw', 'uPitch', 'uBlink', 'uSq', 'uBreath',
  'uMood', 'uPx', 'uLevel', 'uLow', 'uMid', 'uHigh', 'uKick'] as const

export function Figur({ size, drive, label }: {
  size: number
  drive: { current: Drive }
  label: string
}) {
  const ref = useRef<HTMLCanvasElement | null>(null)
  const poke = useRef(0)
  const h = Math.round(size * 0.8)

  useEffect(() => {
    const c = ref.current
    if (!c) return
    const dpr = Math.min(2, window.devicePixelRatio || 1)
    c.width = Math.round(size * dpr)
    c.height = Math.round(h * dpr)
    const gl = c.getContext('webgl2', { alpha: true, premultipliedAlpha: true, antialias: true })
    // No WebGL2: no figure. The page is complete without it.
    if (!gl) return
    const sh = (type: number, src: string) => {
      const s = gl.createShader(type)!
      gl.shaderSource(s, src)
      gl.compileShader(s)
      return s
    }
    const prog = gl.createProgram()!
    gl.attachShader(prog, sh(gl.VERTEX_SHADER, VERT))
    gl.attachShader(prog, sh(gl.FRAGMENT_SHADER, FRAG))
    gl.linkProgram(prog)
    if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) return
    gl.useProgram(prog)
    const buf = gl.createBuffer()
    gl.bindBuffer(gl.ARRAY_BUFFER, buf)
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]), gl.STATIC_DRAW)
    const loc = gl.getAttribLocation(prog, 'p')
    gl.enableVertexAttribArray(loc)
    gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0)
    const U = Object.fromEntries(UNIFORMS.map((k) => [k, gl.getUniformLocation(prog, k)]))

    // Less of everything for somebody who asked their system for less motion:
    // it still answers the sound, it just does not dart about.
    const calm = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false
    const amp = calm ? 0.4 : 1

    const s = {
      yaw: .35, pitch: 0, from: [.35, 0], to: [.35, 0], dartAt: -10,
      queue: [] as { x: number; y: number; at: number }[],
      tx: 0, ty: 0, sq: 1, vsq: 0,
      pointer: null as null | { x: number; y: number; at: number },
      nextGlance: 0, nextBlink: 1.2, blinkAt: -1, double: false, noticed: false,
      env: { level: 0, low: 0, mid: 0, high: 0, kick: 0, prev: 0, ph: 0 },
      poked: 0,
    }
    const onMove = (e: PointerEvent) => {
      const r = c.getBoundingClientRect()
      const x = (e.clientX - (r.left + r.width / 2)) / (r.width * 1.6)
      const y = ((r.top + r.height / 2) - e.clientY) / (r.height * 1.6)
      s.pointer = { x: Math.max(-1, Math.min(1, x)), y: Math.max(-1, Math.min(1, y)), at: performance.now() / 1000 }
    }
    if (!calm) window.addEventListener('pointermove', onMove)

    let raf = 0
    let last = performance.now() / 1000
    const start = last
    const frame = () => {
      raf = requestAnimationFrame(frame)
      const now = performance.now() / 1000
      const dt = Math.min(.05, now - last)
      last = now
      const t = now - start
      if (poke.current !== s.poked) { s.poked = poke.current; s.vsq -= 3.2 }

      // Like a squirrel: darts most of the way at once, eases the rest, holds.
      const dart = (x: number, y: number) => {
        s.from = [s.yaw, s.pitch]
        s.to = [.35 + x * .75, -y * .45]
        s.dartAt = t; s.tx = x; s.ty = y
        s.vsq -= .25
      }

      // The sound, enveloped: fast attack, slow release, so a level that
      // arrives noisy still moves like a muscle and not like a meter.
      const d = drive.current
      const tg = { level: d.level * amp, low: d.low * amp, mid: d.mid * amp, high: d.high * amp }
      const env = s.env
      const fol = (k: 'level' | 'low' | 'mid' | 'high', a: number, r: number) => {
        const x = tg[k]
        env[k] += (x - env[k]) * (1 - Math.exp(-dt / (x > env[k] ? a : r)))
      }
      fol('level', .06, .45); fol('low', .12, .6); fol('mid', .05, .3); fol('high', .03, .2)
      // A transient: the level jumped. Kick the squash spring and the rings.
      const jump = tg.level - env.prev
      if (jump > .12) { env.kick = Math.min(1, env.kick + jump * 2.5); s.vsq -= 6 * jump }
      env.prev += (tg.level - env.prev) * (1 - Math.exp(-dt / .08))
      env.kick *= Math.exp(-dt / .18)
      env.ph += dt * (.5 + 1.4 * env.mid)
      const flutter = 1 + .12 * env.high * Math.sin(t * 31) + .08 * env.mid * Math.sin(t * 13)
      // It notices the loud part: eyes snap to you, once, and hold.
      if (tg.level > .85 && !s.noticed) { s.noticed = true; dart(0, .08); s.nextGlance = t + 2.5 }
      else if (tg.level < .6) s.noticed = false

      if (s.pointer && now - s.pointer.at < 2.5) {
        if (Math.hypot(s.pointer.x - s.tx, s.pointer.y - s.ty) > .15) dart(s.pointer.x, s.pointer.y)
      } else if (s.queue.length && t > s.queue[0]!.at) {
        const g = s.queue.shift()!
        dart(g.x, g.y)
      } else if (!calm && t > s.nextGlance) {
        const centre = Math.random() < .3
        const x = centre ? 0 : (Math.random() * 2 - 1) * .7
        const y = centre ? .05 : Math.random() * .7 - .25
        const bx = s.tx, by = s.ty
        dart(x, y)
        if (Math.random() < .2) s.queue.push({ x: bx * .6 + x * .4, y: by * .6 + y * .4, at: t + .35 }, { x, y, at: t + .62 })
        s.nextGlance = t + 2.4 + Math.random() * 3.5
      }
      const u = t - s.dartAt, quick = .14, slow = 1.3
      const f = u < quick
        ? .85 * (1 - Math.pow(2, -10 * u / quick)) / (1 - Math.pow(2, -10))
        : .85 + .15 * (.5 - .5 * Math.cos(Math.PI * Math.min(1, (u - quick) / slow)))
      s.yaw = s.from[0]! + (s.to[0]! - s.from[0]!) * f + .012 * Math.sin(t * .7)
      s.pitch = s.from[1]! + (s.to[1]! - s.from[1]!) * f + .008 * Math.sin(t * .9 + 1)
      s.vsq += (200 * (1 - s.sq) - 10 * s.vsq) * dt
      s.sq = Math.max(.78, Math.min(1.18, s.sq + s.vsq * dt))

      if (t > s.nextBlink) { s.blinkAt = t; s.double = Math.random() < .2; s.nextBlink = t + 1 + Math.random() * 2 }
      const bt = t - s.blinkAt
      const close = (x: number) => 1 - .92 * Math.pow(Math.sin(Math.PI * Math.min(1, x / .2)), .6)
      let lid = 1
      if (bt >= 0 && bt < .2) lid = close(bt)
      else if (s.double && bt >= .28 && bt < .48) lid = close(bt - .28)

      gl.viewport(0, 0, c.width, c.height)
      gl.clearColor(0, 0, 0, 0)
      gl.clear(gl.COLOR_BUFFER_BIT)
      gl.uniform1f(U.uT!, env.ph)
      gl.uniform1f(U.uAspect!, c.width / c.height)
      gl.uniform1f(U.uYaw!, s.yaw)
      gl.uniform1f(U.uPitch!, s.pitch)
      gl.uniform1f(U.uBlink!, lid)
      gl.uniform1f(U.uSq!, s.sq)
      gl.uniform1f(U.uBreath!, (.505 + .012 * Math.sin(Math.PI * .6 * t)) / .5)
      gl.uniform1f(U.uMood!, 0)
      gl.uniform1f(U.uPx!, 2 / c.height)
      gl.uniform1f(U.uLevel!, Math.min(1, env.level * flutter))
      gl.uniform1f(U.uLow!, env.low)
      gl.uniform1f(U.uMid!, env.mid)
      gl.uniform1f(U.uHigh!, env.high)
      gl.uniform1f(U.uKick!, env.kick)
      gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4)
    }
    raf = requestAnimationFrame(frame)
    return () => {
      cancelAnimationFrame(raf)
      window.removeEventListener('pointermove', onMove)
      gl.getExtension('WEBGL_lose_context')?.loseContext()
    }
  }, [size, h, drive])

  return (
    <canvas ref={ref} role="img" aria-label={label}
      onPointerDown={() => { poke.current++ }}
      style={{ display: 'block', width: size, height: h, touchAction: 'manipulation' }} />
  )
}
